import { addHours } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { getShiftTimezone } from 'lib/shift'
import { shouldShowApproveButton } from 'lib/work'
import { LucideClock4, LucideHourglass, LucideInfo } from 'lucide-react'
import type React from 'react'
import { useMemo } from 'react'
import { Flex, Label, Text } from 'ui'
import {
  countChargedExtraWorkers,
  describeExtraWorkers,
} from 'pages/HomePage/ShiftCard/cushionExtraWorkers'
import { CushionedWorkersModal } from './alerts/CushionedWorkersModal'
import { MinPayPolicyModal } from './alerts/MinPayPolicyModal'
import { TimesheetReviewPeriodModal } from './alerts/TimesheetReviewPeriodModal'
import { useShiftDetail } from './useShiftDetail'

export const ShiftDetailWorkersAlerts = () => {
  const { shift } = useShiftDetail()

  if (!shift) return null

  const assignedWork = shift.work || []
  const numWorkersAssigned = assignedWork.length

  const content = useMemo(() => {
    const contentList: React.ReactNode[] = []
    const workersNeeded = shift?.workersNeeded || 0
    const extraWorkers = workersNeeded ? numWorkersAssigned - workersNeeded : 0
    const chargeAfterMinutes =
      shift?.cushionChargePolicy?.chargeAfterMinutes ?? null
    const chargedExtraWorkers = countChargedExtraWorkers({
      workers: assignedWork,
      workersNeeded,
      shiftStartsAt: shift?.startsAt || '',
      chargeAfterMinutes,
    })
    const shiftHasEnded = shift?.endsAt
      ? new Date(shift.endsAt).getTime() <= Date.now()
      : false
    const extraWorkersNotice = describeExtraWorkers({
      extraWorkers,
      chargedExtraWorkers,
      chargeAfterMinutes,
      shiftHasEnded,
    })
    if (extraWorkersNotice) {
      contentList.push(
        <CushionedWorkersModal
          trigger={
            <Label
              variant={
                extraWorkersNotice.chargesExtraWorkers
                  ? 'warning'
                  : 'information'
              }
              size="large"
              mr={3}
              style={{ cursor: 'pointer' }}
              key="cushioned-workers-label"
            >
              <Flex flexDirection="row" alignItems={'center'}>
                <Text mr={2}>{extraWorkersNotice.label}</Text>
                <LucideInfo size={16} />
              </Flex>
            </Label>
          }
        />
      )
    }

    // if we are:
    // - past the shift end time
    // - and there is still work that needs approval
    // add the "timesheet approval hours" label
    const shiftEndTime = shift?.endsAt ? new Date(shift?.endsAt) : null
    const hasWorkThatNeedsApproval = assignedWork.some((work) =>
      shouldShowApproveButton(work)
    )
    if (hasWorkThatNeedsApproval && shiftEndTime && shiftEndTime < new Date()) {
      const formattedDate = formatInTimeZone(
        addHours(shiftEndTime, shift.shiftEndWorkApprovalDelayHours),
        getShiftTimezone(shift),
        'eee MMM do, h:mma zzz'
      )
      contentList.push(
        <TimesheetReviewPeriodModal
          trigger={
            <Label
              variant="neutral"
              size="large"
              mr={3}
              style={{ cursor: 'pointer' }}
              key="timesheet-review-period-label"
            >
              <Flex flexDirection="row" alignItems={'center'}>
                <LucideHourglass size={16} />
                <Text ml={2}>Approve by {formattedDate}</Text>
              </Flex>
            </Label>
          }
        />
      )
    }

    const minPayLengthMins = shift?.minimumPayPolicy?.minPayLength
    if (minPayLengthMins) {
      const minPayLengthHours = minPayLengthMins / 60
      contentList.push(
        <MinPayPolicyModal
          trigger={
            <Label
              variant="neutral"
              size="large"
              mr={3}
              style={{ cursor: 'pointer' }}
              key="min-pay-policy-label"
            >
              <Flex flexDirection="row" alignItems={'center'}>
                <LucideClock4 size={16} />
                <Text ml={2}>{minPayLengthHours}hr Min Pay Policy</Text>
              </Flex>
            </Label>
          }
        />
      )
    }

    return contentList
  }, [shift])

  return (
    <Flex flexDirection={'row'} flexWrap={'wrap'} alignItems={'center'} mb={3}>
      {content}
    </Flex>
  )
}
